import React, { useCallback, useMemo, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { AgGrid, Flex, Grid } from "@jsq/ds/core";
import { actionButtonCellClass } from "#common/components/ActionButton/styles_vanilla.css";
import { PaymentMethodInputEnum } from "#graphql/supergraphEnums";
import {
  PaymentMethod,
  PaymentServicePaymentBatchStatus,
  PaymentStatus,
} from "#graphql/supergraphTypes";
import { DEFAULT_PAGE_SIZE } from "../constants";
import Money, { MoneyType } from "../Money";
import { usePaymentsContext } from "../PaymentsListWorkflow/Context/PaymentsContext";
import { sideBarDefs } from "../PaymentsListWorkflow/PaymentBatchesTable/Factories/sidebarFactory";
import { TextWithIcon } from "../TextWithIcon";
import Payee from "./Payee";
import { PaymentReferenceDetailRenderer } from "./PaymentReferenceDetailRenderer";
import PositionActionMenu from "./PositionActionMenu";
import { PositionsGqlDataSource } from "./positionsGQLDataSource";
import { positionsGrid } from "./styles_vanilla.css";
import { PositionRowItem } from "./types";
import { positionStatusUI } from "./utils/positionStatusUtil";

export type Payments = {
  objectId: string;
  status: PaymentStatus;
  method: PaymentMethod;
  amount: MoneyType;
};

export type PositionProps = {
  id: string;
  batchId: string | undefined;
  hostName: string;
  payments: Payments[];
  batchStatus: PaymentServicePaymentBatchStatus;
};

export const PositionsTable = (props: PositionProps) => {
  const isTransmitted: boolean = props.batchStatus == "TRANSMITTED";
  const { isGPX } = usePaymentsContext();
  const [rowData, setRowData] = useState<PositionRowItem[]>([]);
  const client = useApolloClient();
  const positionsDataSource = useMemo(
    () => new PositionsGqlDataSource(client, props),
    [client, props],
  );
  const gridApiRef = useRef<AgGrid.GridApi | null>(null);

  const handlePaymentUpdated = useCallback(() => {
    if (gridApiRef.current) {
      gridApiRef.current.showLoadingOverlay();
    }
    positionsDataSource
      .getPositionData()
      .then((response) => {
        setRowData(response);
        if (gridApiRef.current) {
          gridApiRef.current.hideOverlay();
        }
      })
      .catch(() => {
        if (gridApiRef.current) {
          gridApiRef.current.showNoRowsOverlay();
        }
      });
  }, [positionsDataSource]);

  const filterValueGetter = (params: AgGrid.ValueGetterParams<PositionRowItem>) => {
    const amount = params?.data?.amount?.amount ?? 1;
    const power = params?.data?.amount?.precision ?? 0;
    return amount / Math.pow(10, power);
  };

  const detailCellRenderer = PaymentReferenceDetailRenderer;

  const isRowMaster = useCallback((dataItem: PositionRowItem) => {
    if (!dataItem) {
      return false;
    }
    const hasReferenceNumber = !!dataItem.referenceNumber;
    const isTransmittedStatus =
      dataItem.status === "SETTLED" || dataItem.status === "PROCESSING";

    return (hasReferenceNumber && isTransmittedStatus) || dataItem.status === "FAILED";
  }, []);

  const gridOptions: AgGrid.GridOptions = {
    suppressCellFocus: true,
    rowHeight: 42,
    masterDetail: true,
    detailRowAutoHeight: true,
    detailCellRenderer,
    isRowMaster,
  };

  // Build base columns (always included)
  const columns: AgGrid.ColDef[] = [
    {
      field: "payee",
      headerName: "Payee",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      enablePivot: true,
      minWidth: 200,
      flex: 1.5,
      cellRenderer: "agGroupCellRenderer",
      cellRendererParams: (params: AgGrid.ICellRendererParams<PositionRowItem>) => ({
        innerRenderer: Payee,
        innerRendererParams: {
          hostName: params.data?.hostName,
          positionId: params.data?.positionId,
          payee: params.data?.payee,
        },
      }),
    },
    {
      field: "status",
      headerName: "Status",
      enableRowGroup: true,
      enablePivot: true,
      hide: !isTransmitted,
      cellRenderer: TextWithIcon,
      cellRendererParams: ({ data }: { data: PositionRowItem }) => {
        return positionStatusUI(data.status);
      },
      cellStyle: { alignContent: "center" },
      minWidth: 150,
    },
    {
      field: "paymentInstructions",
      headerName: "Payment Instructions",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      enablePivot: true,
      minWidth: 150,
    },
    {
      field: "method",
      headerName: "Method",
      filter: "agSetColumnFilter",
      filterParams: {
        defaultToNothingSelected: true,
        values: Object.keys(PaymentMethodInputEnum),
      },
      enableRowGroup: true,
      enablePivot: true,
      maxWidth: 100,
      flex: 0.7,
    },
    {
      field: "amount",
      headerName: "Amount",
      filter: "agNumberColumnFilter",
      filterValueGetter: filterValueGetter,
      enableRowGroup: true,
      enablePivot: true,
      minWidth: 100,
      cellRenderer: Money,
      cellRendererParams: ({ data }: { data: PositionRowItem }) => ({
        money: data.amount,
        rounded: false,
      }),
    },
  ];

  // Actions column definition (conditionally included)
  const actionsColumn: AgGrid.ColDef = {
    field: "actions",
    headerName: "Actions",
    sortable: false,
    filter: false,
    resizable: false,
    suppressMenu: true,
    hide: !isTransmitted,
    width: 60,
    minWidth: 60,
    maxWidth: 60,
    cellClass: actionButtonCellClass,
    cellStyle: { overflow: "visible", zIndex: -1 },
    cellRenderer: ({ data }: { data: PositionRowItem }) => (
      <PositionActionMenu
        paymentId={data.paymentId || ""}
        payeeName={data.payee || ""}
        paymentStatus={data.status}
        onPaymentUpdated={handlePaymentUpdated}
      />
    ),
  };

  // Conditionally include actions column: only when not GPX
  // This completely removes the column from the table (not just hiding it)
  // so it won't appear in the Columns sidebar toggle for GPX users
  if (!isGPX) {
    columns.push(actionsColumn);
  }

  const colDefs = [
    {
      headerName: "Positions",
      children: columns,
    },
  ];

  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      minWidth: 100,
      filter: true,
    };
  }, []);

  const autoGroupColumnDef = useMemo(() => {
    return {
      minWidth: 200,
    };
  }, []);

  const onGridReady = (event: AgGrid.GridReadyEvent) => {
    gridApiRef.current = event.api;
    event.api.showLoadingOverlay();
    positionsDataSource
      .getPositionData()
      .then((response) => {
        setRowData(response);
      })
      .catch(() => {
        event.api.showNoRowsOverlay();
      });
  };
  return (
    <Flex className={positionsGrid} data-testid="positionsTable">
      <Grid
        rowData={rowData}
        gridOptions={gridOptions}
        columnDefs={colDefs}
        defaultColDef={defaultColDef}
        autoGroupColumnDef={autoGroupColumnDef}
        sideBar={sideBarDefs()}
        onGridReady={onGridReady}
        domLayout="autoHeight"
        pagination={true}
        paginationPageSize={DEFAULT_PAGE_SIZE}
        paginationPageSizeSelector={false}
        suppressColumnVirtualisation={true}
        suppressRowVirtualisation={true}
      />
    </Flex>
  );
};
